/**
 * SiteTypeModal — 86bc7raat. Add / edit one platform site type.
 *
 * Mirrors TankModal. The one asymmetry worth noticing: `code` is editable only when
 * creating. It is the stable machine identifier that `sites` rows and the mobile
 * catalog match on, so renaming one would silently orphan every site that referenced
 * the old value — the API has no PATCH path for it either, so this is not merely a UI
 * restriction.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle } from '../../ui/bits';
import { useCreateSiteType, useUpdateSiteType, type SiteType } from './hooks';

export function SiteTypeModal({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: SiteType | null;
}): React.JSX.Element {
  const [code, setCode] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('0');

  // Reset the form whenever the modal opens, so a previous edit never leaks into a
  // subsequent add.
  React.useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? '');
    setLabel(editing?.label ?? '');
    setSortOrder(String(editing?.sortOrder ?? 0));
  }, [open, editing]);

  const close = (): void => onOpenChange(false);
  const create = useCreateSiteType(close);
  const update = useUpdateSiteType(editing?.id ?? '');
  const busy = create.isPending || update.isPending;

  const parsedSort = Number.parseInt(sortOrder, 10);
  const sortValid = Number.isInteger(parsedSort) && parsedSort >= 0 && parsedSort <= 9999;
  const codeValid = /^[A-Za-z0-9_]{2,40}$/.test(code.trim());
  const canSubmit =
    label.trim().length > 0 && sortValid && (editing !== null || codeValid) && !busy;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    if (editing !== null) {
      update.mutate(
        { label: label.trim(), sortOrder: parsedSort },
        {
          onSuccess: () => {
            toast.success('Site type updated');
            close();
          },
          onError: () => toast.error('Could not update the site type'),
        },
      );
      return;
    }
    create.mutate(
      { code: code.trim(), label: label.trim(), sortOrder: parsedSort },
      {
        onSuccess: () => toast.success('Site type created'),
        // A duplicate code comes back as a 400 with fields[].reason = 'code_taken'.
        onError: () => toast.error('Could not create the site type — is that code already used?'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing !== null ? 'Edit site type' : 'Add site type'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow
            label="Code"
            htmlFor="st-code"
            required={editing === null}
            hint={
              editing !== null
                ? 'The code cannot change — sites reference it.'
                : 'Letters, numbers and underscores. Stored uppercase, and permanent once created.'
            }
          >
            <input
              id="st-code"
              value={code}
              disabled={editing !== null}
              onChange={(e): void => setCode(e.target.value)}
              placeholder="TANK_BATTERY"
              style={fieldStyle}
            />
          </FormRow>

          <FormRow
            label="Label"
            htmlFor="st-label"
            required
            hint="What haulers see in the site-type picker."
          >
            <input
              id="st-label"
              value={label}
              onChange={(e): void => setLabel(e.target.value)}
              placeholder="Tank Battery"
              style={fieldStyle}
            />
          </FormRow>

          <FormRow
            label="Sort order"
            htmlFor="st-sort"
            hint="Lower sorts first. Existing types run 10 to 999."
          >
            <input
              id="st-sort"
              type="number"
              min={0}
              max={9999}
              value={sortOrder}
              onChange={(e): void => setSortOrder(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {busy ? 'Saving…' : editing !== null ? 'Save changes' : 'Create site type'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
